import type { DataTableFilterMeta } from 'primereact/datatable';
import {
  conditionGroupFromJson,
  type ConditionGroup,
  type ConditionLeaf,
  relatedLeavesOf,
  withoutRelatedLeaves,
  type ConditionNode,
} from '@/helpers/conditionTree.helpers';
import type {
  AnalysisJsonObject,
  AnalysisRecipeStep,
  AnalysisSheetRecipe,
} from '@/types/analysisRecipe.types';
import type {
  Pipeline,
  PipelineParamValue,
  PipelineStep,
} from '@/interfaces/pipelines.interface';
import {
  ANALYSIS_FLAG_DEFAULT_LABELS,
  analysisFlagLabels,
  analysisJoinStep,
  normalizeAnalysisJoinConfig,
  analysisLookupOrderBy as lookupOrderBy,
  isAnalysisGeneratedField,
  normalizeAnalysisLookupConfig,
  normalizeAnalysisNumericLiteral,
  normalizeAnalysisRollupMetrics,
  type AnalysisFlagLabels,
  type NormalizedAnalysisRollupMetric,
} from '@/helpers/analysisMadeStep.helpers';
import { analysisRelationshipPathId } from '@/helpers/analysisRelationshipPath.helpers';
import { isAnalysisTotalSheet } from '@/helpers/analysisBreakdown.helpers';
import {
  analysisColumnLabels,
  buildAnalysisColumnDisplay,
} from '@/helpers/analysisColumnDisplay.helpers';
import { orderAnalysisCalcSteps } from '@/helpers/analysisDependencies.helpers';
import {
  DEFAULT_REMAP_SEARCH_LIMIT,
  SEARCH_RELEVANCE_COLUMNS,
} from '@/constants/pipelines.constants';

/**
 * Joins compiler blocker messages into one sentence run.
 *
 * Blockers are written as complete sentences, so appending a full stop around
 * them produced 'a separate sheet.. Related-data columns…' on screen. Ending
 * each one exactly once keeps a blocker author free to punctuate normally.
 */
export const joinBlockerSentences = (blockers: string[]): string =>
  blockers
    .map((blocker) => blocker.trim())
    .filter(Boolean)
    .map((blocker) => (/[.!?]$/.test(blocker) ? blocker : `${blocker}.`))
    .join(' ');

export interface AnalysisBackendCapabilities {
  matchRelated: boolean;
  groupRows: boolean;
  deriveRows: boolean;
  filterRows: boolean;
  windowRows: boolean;
  toTable: boolean;
  relatedPredicates: boolean;
  concatRows: boolean;
  /**
   * `join_on_value` — pairing two labels on an equality of property values,
   * where the schema has no edge between them.
   */
  valueJoin: boolean;
  /** Averaging a related record set, which every rollup aggregates by alias. */
  averageRollup: boolean;
  /**
   * `to_table {include_total: true}`, which reports the pre-pagination row count
   * alongside the page. Purely additive: withheld, the page still returns and
   * only the total degrades to "at least this many", so nothing gates on it.
   */
  tableTotals: boolean;
  /**
   * Deterministic `first`/`last` with an `order_by`, so a lookup over a to-many
   * path returns the same match on every run instead of an arbitrary one.
   */
  orderedPick: boolean;
  /**
   * `group_rows {keys: []}` — folding every row into one grand-total row. What
   * makes a single-value KPI card reachable from a sheet at all.
   */
  globalAggregate: boolean;
  /**
   * A sheet sourced from a MEANING search. There is no pipeline op for it, so a
   * semantic source has nothing to compile to. Held here so such a sheet
   * reports itself unrunnable rather than quietly running a keyword search.
   */
  semanticSearchSource: boolean;
}

export const UNVERIFIED_ANALYSIS_CAPABILITIES: AnalysisBackendCapabilities = {
  matchRelated: false,
  groupRows: false,
  deriveRows: false,
  filterRows: false,
  windowRows: false,
  toTable: false,
  relatedPredicates: false,
  concatRows: false,
  valueJoin: false,
  averageRollup: false,
  semanticSearchSource: false,
  tableTotals: false,
  orderedPick: false,
  globalAggregate: false,
};

/**
 * Capabilities with semantic evidence on the deployed API. A flag flips only on a
 * passing semantic assertion — the returned rows actually meaning what the
 * capability claims — never on an HTTP 200.
 *
 * The evidence came from a live probe harness that has since been removed, so this
 * comment is now the whole record. Keep it that way: state the reasoning for a
 * withheld flag here, because there is no longer a panel a reader can open to
 * re-derive it. Re-proving a flag means writing the pipeline by hand against a
 * dev cluster and reading the rows back.
 *
 * `concatRows` is now true. It was withheld because expression `+` over strings
 * returned a null column — which is still what `+` does. The backend answer is a
 * dedicated `{fn: 'concat', args: […]}` primitive rather than an overloaded
 * operator, and it was verified by joining two string columns and reading the
 * joined value back. So the flag reflects a different compile target, not a
 * changed verdict on the old one: `compileCalcExpression` had to stop mapping
 * `concat` to `+` before this could flip.
 *
 * `averageRollup` stays false, and is the one remaining backend gap. The engine
 * rejects the query outright: an `avg` over a matched alias compiles to a
 * null-safe `CASE` around `count`/`reduce`, and Memgraph answers "Using
 * aggregation functions inside of CASE is not allowed" for every shape of it —
 * optional or required match, source grain or regrouped. The backend's
 * dedup-fold rewrite moved the fold outside the CASE for the aggregation path,
 * but the rollup path still lands inside one. Averaging a plain sheet column
 * still works, so only rollups are gated.
 *
 * `orderedPick`, `tableTotals` and `globalAggregate` verified clean and each gained
 * its flag alongside the code that emits it — deterministic `first`/`last` with an
 * object `order_by`, `to_table {include_total: true}` for an exact pre-pagination
 * total, and `group_rows {keys: []}` for a one-row grand total. A flag no compiler
 * reads would gate nothing and only invite the belief that the feature exists,
 * which is why none of them landed ahead of its emitter.
 *
 * `path_fanout` verified clean too and deliberately has no flag: it is measured by
 * the authoring UI (`usePathFanout`), never emitted by this compiler, so there is
 * nothing here to gate.
 */
export const PROVEN_ANALYSIS_CAPABILITIES: AnalysisBackendCapabilities = {
  matchRelated: true,
  groupRows: true,
  deriveRows: true,
  filterRows: true,
  windowRows: true,
  toTable: true,
  relatedPredicates: true,
  concatRows: true,
  // Verified semantically, not by an HTTP 200. Probe: a `Pump` sheet on
  // ptest_v2 joined to `Pump_Name_XREF` on `pump_name`, returning that label's
  // `petrix_pump_name`. The rows discriminate, because the two fields differ
  // for some keys: `53Q0306` came back as `53Q-0306` and `53Q10400` as
  // `53Q-10400` — the right side's value, keyed correctly, and neither the
  // join key nor the source's own similarly-named column.
  //
  // The same run established what this op does NOT do: it is an inner join
  // (`rowset_join_ops.py` matches the right node with a required MATCH, and the
  // op takes no outer/optional argument), so 349 source records became 341.
  // That is disclosed in the join step's sentence rather than gated here — a
  // capability flag answers "does this work", and it does.
  valueJoin: true,
  averageRollup: false,
  tableTotals: true,
  orderedPick: true,
  globalAggregate: true,
  semanticSearchSource: false,
};

/**
 * Publication surfaces a compiled sheet may reach. Kept apart from
 * `AnalysisBackendCapabilities` because these gate what a sheet may be
 * persisted as, not what its pipeline compiles to, and each is a separate
 * contract: a table card pages a Table carrier, a graph card materialises a
 * Graph, a fragment stores the pipeline under a reusable name.
 *
 * Table, chart and KPI cards persist the sheet's own publication pipeline and
 * re-run it on the dashboard, which is the same `run_pipelines` contract the
 * sheet already executes on screen. Fragment definition stays closed until the
 * define/list/invoke/delete lifecycle has been exercised against the deployed
 * API: the saved-view write is deliberately blocking, so an unproven fragment
 * call would take the whole save down with it.
 *
 * `fragment` stays false. Its lifecycle was exercised against the deployed API and
 * came back 15/17, and the two failures were not cosmetic: any authenticated caller
 * can define, overwrite or delete a fragment under any `project_id`. That is a
 * backend authorization gap, not a frontend one, so this flag waits on the backend
 * rather than on anything here. The harness that produced that verdict has been
 * removed, so re-proving it means exercising define/list/invoke/delete by hand and
 * checking project isolation explicitly.
 */
export interface AnalysisPublicationTargets {
  table: boolean;
  chart: boolean;
  kpi: boolean;
  graph: boolean;
  fragment: boolean;
}

export const ANALYSIS_PUBLICATION_VERIFIED: AnalysisPublicationTargets = {
  table: true,
  chart: true,
  kpi: true,
  graph: true,
  fragment: false,
};

export const ANALYSIS_FRAGMENT_UNVERIFIED_REASON =
  'Saving this sheet as a reusable fragment is not verified against the deployed API yet';

export interface CompileAnalysisRecipeOptions {
  sourceColumns: string[];
  filters?: DataTableFilterMeta;
  sort?: { field: string; direction: 'asc' | 'desc' } | null;
  offset?: number;
  limit?: number;
  capabilities?: AnalysisBackendCapabilities;
  /**
   * `'interactive'` compiles the grid's current page. `'publication'` compiles
   * the pipeline a dashboard card stores: it starts at the first row and adds a
   * tie-break to the sort so the card can page a stable order instead of
   * re-shuffling rows between pages.
   */
  target?: 'interactive' | 'publication';
  /**
   * Ask the sink for the exact pre-pagination row count.
   *
   * Opt-in because it costs the sink a second round-trip on the backend, so only
   * a surface that actually shows a total should pay for it. Silently ignored
   * when the `tableTotals` capability is withheld: the total is additive, and
   * blocking the run over a missing count would trade every row for a number.
   */
  includeTotal?: boolean;
  /**
   * The parent stage's own sort and filters, when compiling a breakdown.
   *
   * A breakdown's `filters`/`sort` describe the grouped grain. Forwarding them
   * into the parent compile asks the source pipeline to order or filter by a
   * column it never projects — a sort on a grouped-only column fails the run
   * outright. The parent stage is compiled from the parent's own view state.
   */
  parentViewState?: {
    filters?: DataTableFilterMeta;
    sort?: { field: string; direction: 'asc' | 'desc' } | null;
  };
}

/**
 * `order_by` for a sink, as `field:dir` parts. A published pipeline always ends
 * with a unique column so paging cannot repeat or skip a row; the interactive
 * grid keeps exactly the order the user asked for.
 */
const sinkOrderBy = (
  options: CompileAnalysisRecipeOptions,
  tieBreak: string | null,
  /**
   * Columns this sink actually projects. A sort naming anything else is
   * dropped rather than emitted: the backend rejects an `order_by` over an
   * unprojected column and fails the whole run, so the sheet renders nothing
   * at all instead of merely being unsorted. Reachable without any bug —
   * a saved view restored onto a sheet whose columns changed, or a metric
   * removed while it was the sort key — so the guard belongs here rather
   * than at each caller. Omitted means "cannot check", and the sort stands.
   */
  projectedColumns?: string[],
): string | undefined => {
  const sortable =
    options.sort !== null &&
    options.sort !== undefined &&
    (projectedColumns === undefined ||
      projectedColumns.includes(options.sort.field));
  const parts =
    options.sort && sortable
      ? [`${options.sort.field}:${options.sort.direction}`]
      : [];
  if (
    options.target === 'publication' &&
    tieBreak &&
    options.sort?.field !== tieBreak
  ) {
    parts.push(`${tieBreak}:asc`);
  }
  return parts.length > 0 ? parts.join(',') : undefined;
};

/**
 * `include_total` for a sink, when the caller asked for a total and the backend
 * can report one. Kept as a function so both sinks — the breakdown's and the
 * main recipe's — cannot drift apart on the capability check.
 */
const sinkIncludeTotal = (
  options: CompileAnalysisRecipeOptions,
): { include_total: true } | undefined =>
  options.includeTotal && (options.capabilities?.tableTotals ?? false)
    ? { include_total: true }
    : undefined;

export function compileAnalysisBreakdown(
  parent: AnalysisSheetRecipe,
  child: AnalysisSheetRecipe,
  options: CompileAnalysisRecipeOptions,
): CompiledAnalysisRecipe {
  if (!child.breakdown) {
    return compileAnalysisRecipe(child, options);
  }
  // The parent stage runs at source grain, so it gets the parent's own view
  // state — never the child's, which is written against grouped columns the
  // parent does not project. `sourceColumns`, `capabilities` and `target` still
  // inherit: those describe the projection the child groups over.
  const parentCompiled = compileAnalysisRecipe(parent, {
    ...options,
    filters: options.parentViewState?.filters,
    sort: options.parentViewState?.sort ?? null,
    parentViewState: undefined,
  });
  const steps = parentCompiled.pipeline.steps.filter(
    (step) => step.op !== 'to_table',
  );
  const parentFinal = steps[steps.length - 1]?.as ?? 'source_rows';
  const aggregations = child.breakdown.aggregations ?? [];
  const windows = child.breakdown.windows ?? [];
  // The parent stage runs inside this pipeline, so its columns need words too.
  const columnLabels: Record<string, string> = {
    ...parentCompiled.columnLabels,
    ...analysisColumnLabels(buildAnalysisColumnDisplay(child, parent)),
  };

  // A breakdown carries its own Kept conditions. Those over the grouped grain
  // (counts, percentages) can only run after grouping; the rest narrow the rows
  // that feed it. A single `or` clause spanning both grains cannot be split into
  // two filters, and existence conditions have no place to attach here, so both
  // keep the sheet unexecuted rather than dropping the user's filter silently.
  // No key of either kind = total grain: one row for the whole parent. Read via
  // the shared predicate so a *broken* breakdown, which is also missing its
  // column, cannot be mistaken for a deliberate total.
  const isTotal = isAnalysisTotalSheet(child);
  const groupByColumn = child.breakdown.groupByColumn;
  // A breakdown carries calculations like any other sheet. They compile to a
  // `derive_rows` over the grouped stage — the form the backend documents for a
  // post-`group_rows` RowSet (`{col}` refs; `{field}` is seed-only) — so a
  // grouped sheet can divide one summary by another. `operandExpression` always
  // emits `{col}`, so nothing here has to translate the operands.
  const childCalcSteps = child.steps.filter((step) => step.kind === 'calc');
  const {
    ordered: orderedChildCalcSteps,
    unresolved: unresolvedChildCalcSteps,
  } = orderAnalysisCalcSteps(childCalcSteps);
  const childCalcLiteralBlockers: string[] = [];
  const compilableChildCalcSteps = orderedChildCalcSteps.filter((step) => {
    const reason = invalidCalcLiteralReason(step);
    if (reason) childCalcLiteralBlockers.push(reason);
    return reason === null;
  });
  const childCalcColumns = compilableChildCalcSteps
    .map((step) => step.outputColumns[0])
    .filter((column): column is string => Boolean(column));
  const groupedOnlyColumns = new Set([
    ...aggregations.map((aggregation) => aggregation.outputColumn),
    ...windows.map((window) => window.outputColumn),
    // A calc column only exists after the grouping, so a Kept condition reading
    // one belongs to the grouped grain like any aggregate.
    ...childCalcColumns,
  ]);
  const childKeepTree = conditionGroupFromJson(child.keepFilter);
  const childKeptPredicates = [
    ...appliedControlPredicates(child),
    ...compileConditionPredicates(withoutRelatedLeaves(childKeepTree)),
    // The breakdown's own column filters. These used to be dropped entirely,
    // which became visible once a grouped column could be filtered from its
    // column menu: the filter applied to nothing and said so nowhere.
    ...activeFilterPredicates(options.filters),
  ];
  const sourceGrainPredicates: PredicateLeaf[] = [];
  const groupedGrainPredicates: PredicateLeaf[] = [];
  let keptIsCompilable = relatedConditionCount(childKeepTree) === 0;
  childKeptPredicates.forEach((predicate) => {
    const columns = predicateColumns(predicate);
    const readsGrouped = columns.some((column) =>
      groupedOnlyColumns.has(column),
    );
    const readsSourceGrain = columns.some(
      (column) => !groupedOnlyColumns.has(column) && column !== groupByColumn,
    );
    if (readsGrouped && readsSourceGrain) {
      keptIsCompilable = false;
      return;
    }
    (readsGrouped ? groupedGrainPredicates : sourceGrainPredicates).push(
      predicate,
    );
  });

  // Group keys follow the kind of RowSet being grouped. Node-backed rows — the
  // source projection, with filters or derivations on top — take `{field}`;
  // once a `group_rows` has run the backend only accepts a prior-stage column
  // as `{col}`. The wrong form fails the whole run, so it is read off the
  // pipeline built so far rather than assumed.
  const groupByRelated = child.breakdown.groupByRelated;
  // A parent whose own Made steps (lookups/rollups) aggregated into
  // `grain_preserved` (see `compileAnalysisRecipe`'s own `group_rows` push) has
  // already left `n`'s node binding behind — Cypher only carries the projected
  // columns past a `WITH`/aggregation clause. `groupByRelated`'s own hop chain
  // starts every `match_related` from node alias `n`, so it is unsatisfiable
  // here in exactly the way `groupKeyIsColumn` already detects for a plain
  // column key; unlike a plain key, though, there is no column-typed form of
  // `match_related` to fall back to, so this blocks the sheet instead.
  const parentEndsInGroupRows = steps.some((step) => step.op === 'group_rows');
  const groupByRelatedNeedsParentNode = Boolean(
    groupByRelated && parentEndsInGroupRows,
  );
  const groupKeyIsColumn =
    !groupByRelated &&
    groupByColumn !== undefined &&
    (isAnalysisGeneratedField(groupByColumn) || parentEndsInGroupRows);
  const groupInput = keptIsCompilable
    ? pushFilterRows(
        steps,
        parentFinal,
        sourceGrainPredicates,
        'breakdown_kept',
      )
    : parentFinal;
  let current: string;
  if (groupByRelated) {
    // One-hop-per-step, `optional: true` throughout: a source row with no
    // match must survive as its own group rather than vanish from the count
    // entirely, which is what a required MATCH would do. Mirrors the same
    // chain `compileAnalysisRecipe` builds for a multi-hop lookup — see its
    // `match_related` loop — just against this breakdown's own steps array.
    let joinCursor = groupInput;
    let sourceAlias = 'n';
    let targetAlias = sourceAlias;
    groupByRelated.path.hops.forEach((hop, hopIndex) => {
      targetAlias = `breakdown_related_${hopIndex}`;
      const matched = `breakdown_related_matched_${hopIndex}`;
      steps.push({
        op: 'match_related',
        from: joinCursor,
        args: {
          from_alias: sourceAlias,
          rel: hop.relationship,
          direction: hop.direction,
          target_label: hop.targetLabel,
          target_alias: targetAlias,
          optional: true,
        },
        as: matched,
      });
      joinCursor = matched;
      sourceAlias = targetAlias;
    });
    // Grouping directly on the target's field would surface an internal name
    // for every row with no match — Cypher groups a null key into one bucket
    // on its own, but that bucket still needs the "No <Label>" a reader
    // expects instead of a blank cell. Named exactly like a lookup's own
    // blank-fill (`__analysis_lookup_raw_*`): group into a raw column, then
    // `derive_rows` immediately after resolves the public name from it.
    const rawGroupColumn = `__analysis_breakdown_raw_${groupByColumn}`;
    columnLabels[rawGroupColumn] = groupByRelated.targetLabel;
    steps.push({
      op: 'group_rows',
      from: joinCursor,
      args: {
        keys: [
          {
            node: targetAlias,
            field: groupByRelated.displayField,
            as: rawGroupColumn,
          },
        ],
        aggregations: aggregations.map((aggregation) =>
          aggregation.op === 'count_distinct'
            ? {
                // The auto "rows" metric: how many DISTINCT source records
                // reach this group, not how many (source, target) pairs the
                // join produced — the same `count_distinct(s_id)` choice
                // `group_by_target`'s own `rollup='by_target'` row count
                // makes, for the identical reason (a plain `count` here would
                // count each match, inflating a single source's contribution
                // by however many times it fans out into unrelated groups).
                name: aggregation.outputColumn,
                op: 'count_distinct',
                node: 'n',
                field: 'id',
              }
            : {
                name: aggregation.outputColumn,
                op: aggregation.op,
                // Not deduped: a source connected to three targets sums into
                // all three groups. That is the same "counts in every group
                // it's connected to" rule the row count follows, generalised
                // to a sum/min/max — the join fans a row out once per match on
                // purpose, and a value aggregation reads every fanned-out row.
                // `col`, not `node: 'n'`: a carried metric can be a calc
                // column from an earlier `derive_rows` stage, which is not a
                // graph property `n` has — same reason the plain branch below
                // reads every carried metric through `col` too.
                ...(aggregation.field ? { col: aggregation.field } : {}),
              },
        ),
      },
      as: 'breakdown_grouped_raw',
    });
    steps.push({
      op: 'derive_rows',
      from: 'breakdown_grouped_raw',
      args: {
        derivations: [
          {
            name: groupByColumn,
            expr: compileFirstNonEmptyExpression([
              { col: rawGroupColumn },
              { lit: `No ${groupByRelated.targetLabel}` },
            ]),
          },
        ],
      },
      as: 'breakdown_grouped',
    });
    current = 'breakdown_grouped';
  } else if (isTotal) {
    // `keys: []` is the grand-total grain: every row folds into one. Totalling an
    // already-grouped parent is legitimate — a total of group counts is a real
    // question — so unlike `groupByRelated` this does not care whether the parent
    // ended in a `group_rows`. Aggregation fields go through `col` for the same
    // reason the plain branch does: a carried metric can be a calc column that
    // `n` has no property for.
    steps.push({
      op: 'group_rows',
      from: groupInput,
      args: {
        keys: [],
        aggregations: aggregations.map((aggregation) => ({
          name: aggregation.outputColumn,
          op: aggregation.op,
          ...(aggregation.field ? { col: aggregation.field } : {}),
        })),
      },
      as: 'breakdown_grouped',
    });
    current = 'breakdown_grouped';
  } else {
    steps.push({
      op: 'group_rows',
      from: groupInput,
      args: {
        keys: [
          groupKeyIsColumn
            ? { col: groupByColumn, as: groupByColumn }
            : { node: 'n', field: groupByColumn, as: groupByColumn },
        ],
        aggregations: aggregations.map((aggregation) => ({
          name: aggregation.outputColumn,
          op: aggregation.op,
          ...(aggregation.field ? { col: aggregation.field } : {}),
        })),
      },
      as: 'breakdown_grouped',
    });
    current = 'breakdown_grouped';
  }
  if (windows.length) {
    steps.push({
      op: 'window_rows',
      from: current,
      args: {
        windows: windows.map((window) => ({
          name: window.outputColumn,
          op: window.op === 'percent_of_total' ? 'pct_of_total' : window.op,
          col: window.sourceColumn,
          order: window.op === 'rank' ? 'desc' : undefined,
        })),
      },
      as: 'breakdown_windowed',
    });
    current = 'breakdown_windowed';
  }
  // After the windows so a calculation may read a percentage or rank, and
  // before the grouped Kept filter so that filter may read a calculation.
  compilableChildCalcSteps.forEach((step, index) => {
    const as = `breakdown_derived_${index}`;
    steps.push({
      op: 'derive_rows',
      from: current,
      args: {
        derivations: [
          {
            name: step.outputColumns[0],
            expr: compileCalcExpression(step),
          },
        ],
      },
      as,
    });
    current = as;
  });
  if (keptIsCompilable) {
    current = pushFilterRows(
      steps,
      current,
      groupedGrainPredicates,
      'breakdown_grouped_kept',
    );
  }
  const outputColumns = [
    ...(groupByColumn ? [groupByColumn] : []),
    ...aggregations.map((aggregation) => aggregation.outputColumn),
    ...windows.map((window) => window.outputColumn),
    ...childCalcColumns,
  ];
  // A total has one row, so there is nothing to order and no tie to break. The
  // allowlist still applies: a stale sort naming the parent's column must not
  // reach a sink that no longer projects it.
  const breakdownOrderBy = sinkOrderBy(
    options,
    groupByColumn ?? null,
    outputColumns,
  );
  steps.push({
    op: 'to_table',
    from: current,
    args: {
      ...(breakdownOrderBy ? { order_by: breakdownOrderBy } : {}),
      offset: options.target === 'publication' ? 0 : (options.offset ?? 0),
      ...(options.limit !== undefined ? { limit: options.limit } : {}),
      ...sinkIncludeTotal(options),
    },
    as: 'out',
  });
  const keptPredicateCount =
    sourceGrainPredicates.length + groupedGrainPredicates.length;
  // A breakdown marked broken lost a column it groups or aggregates by, so the
  // backend rejects the re-group outright ("… is not a RowSet column"). Report it
  // as a recipe blocker rather than letting it compile as runnable: publication
  // and export both gate on `executable`, and a broken sheet would otherwise be
  // publishable into a permanently failing dashboard card.
  const parentBlockers = [
    ...parentCompiled.blockers,
    ...(child.breakdown.status === 'broken'
      ? [
          child.breakdown.brokenReason
            ? `“${child.name}” is broken: ${child.breakdown.brokenReason}`
            : `“${child.name}” is broken: a source column it groups by was removed`,
        ]
      : []),
    ...(groupByRelatedNeedsParentNode
      ? [
          `“${child.name}” cannot group by a related record: the parent sheet's own related-data columns (a lookup or summary) already aggregate its rows, leaving no source record left to traverse from. Remove those columns first, or group by one of them directly instead.`,
        ]
      : []),
    ...unresolvedChildCalcSteps.map((step) => {
      const label =
        typeof step.config.outputColumn === 'string' && step.config.outputColumn
          ? step.config.outputColumn
          : step.outputColumns[0];
      return `“${label}” reads a calculation that is produced after it, or itself; point it at an earlier column`;
    }),
    ...childCalcLiteralBlockers,
  ];
  const missingCapabilities = [
    ...new Set([
      ...parentCompiled.missingCapabilities,
      ...(!options.capabilities?.groupRows ? ['groupRows' as const] : []),
      ...(windows.length && !options.capabilities?.windowRows
        ? ['windowRows' as const]
        : []),
      ...(keptPredicateCount > 0 && !options.capabilities?.filterRows
        ? ['filterRows' as const]
        : []),
      ...(keptIsCompilable ? [] : ['filterRows' as const]),
      // `group_rows {keys: []}` is its own contract: an engine that groups by a
      // key is not automatically one that folds every row into a grand total.
      ...(isTotal && !options.capabilities?.globalAggregate
        ? ['globalAggregate' as const]
        : []),
      ...(compilableChildCalcSteps.length && !options.capabilities?.deriveRows
        ? ['deriveRows' as const]
        : []),
      ...(compilableChildCalcSteps.some(
        (step) => String(step.config.operator) === 'concat',
      ) && !options.capabilities?.concatRows
        ? ['concatRows' as const]
        : []),
    ]),
  ];
  return {
    pipeline: {
      name: `analysis_sheet_${child.id.replace(/[^a-zA-Z0-9_-]/g, '_')}`,
      steps,
      emit: 'last',
    },
    executable: missingCapabilities.length === 0 && parentBlockers.length === 0,
    missingCapabilities,
    blockers: parentBlockers,
    outputColumns,
    // A total row has no key: there is exactly one, so nothing needs
    // identifying, and naming a column the sink does not project would be a lie.
    rowKey: groupByColumn ?? null,
    columnLabels,
  };
}

export interface CompiledAnalysisRecipe {
  pipeline: Pipeline;
  executable: boolean;
  missingCapabilities: Array<keyof AnalysisBackendCapabilities>;
  /**
   * Recipe-level reasons the pipeline cannot run, in the user's terms. Distinct
   * from `missingCapabilities`: these are fixable by editing the recipe, not by
   * the backend gaining a contract.
   */
  blockers: string[];
  outputColumns: string[];
  /** `null` for a total sheet — one row, so no row identity to carry. */
  rowKey: string | null;
  /**
   * A reader label for every column the pipeline generates, internal ones
   * included, so the backend can word the run's steps without encoded keys.
   */
  columnLabels: Record<string, string>;
}

interface NormalizedLookupOutput {
  outputColumn: string;
  field: string;
  hopIndex: number;
  condition: ConditionGroup | null;
  /** Author-chosen value for rows where the lookup found nothing. */
  ifBlank: string | number | null;
}

const normalizedLookupOutputs = (
  step: AnalysisRecipeStep,
  terminalHopIndex: number,
): NormalizedLookupOutput[] => {
  const config = normalizeAnalysisLookupConfig(step);
  if (config.configuredOutputs !== null) {
    return config.configuredOutputs.flatMap((output) =>
      output.outputColumn
        ? [
            {
              outputColumn: output.outputColumn,
              field: output.field,
              hopIndex: output.hopIndex ?? terminalHopIndex,
              condition: output.condition
                ? conditionGroupFromJson(output.condition)
                : null,
              ifBlank: output.ifBlank,
            },
          ]
        : [],
    );
  }

  const intermediate = config.intermediateOutputs.flatMap((output) =>
    output.outputColumn && output.hopIndex !== null
      ? [
          {
            outputColumn: output.outputColumn,
            field: output.field,
            hopIndex: output.hopIndex,
            condition: output.condition
              ? conditionGroupFromJson(output.condition)
              : null,
            ifBlank: null,
          },
        ]
      : [],
  );
  return [
    ...intermediate,
    ...config.targetFields.flatMap((field, index) => {
      const outputColumn = step.outputColumns[intermediate.length + index];
      return outputColumn
        ? [
            {
              outputColumn,
              field,
              hopIndex: terminalHopIndex,
              ifBlank: null,
              condition: config.condition
                ? conditionGroupFromJson(config.condition)
                : null,
            },
          ]
        : [];
    }),
  ];
};

interface CompiledRollupMetric extends Omit<
  NormalizedAnalysisRollupMetric,
  'condition'
> {
  outputColumn: string;
  condition: ConditionGroup | null;
}

const compiledRollupMetrics = (
  metrics: NormalizedAnalysisRollupMetric[],
): CompiledRollupMetric[] =>
  metrics.flatMap((metric) =>
    metric.outputColumn
      ? [
          {
            ...metric,
            outputColumn: metric.outputColumn,
            condition: metric.condition
              ? conditionGroupFromJson(metric.condition)
              : null,
          },
        ]
      : [],
  );

const CALC_OPERATOR_TO_PIPELINE: Record<string, string> = {
  add: '+',
  subtract: '-',
  multiply: '*',
  divide: '/',
};

const PREDICATE_OPERATOR_TO_PIPELINE: Record<string, string> = {
  is: '=',
  equals: '=',
  isNot: '!=',
  notEquals: '!=',
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
  isOneOf: 'in',
  isBlank: 'missing',
  notBlank: 'non_empty',
};

const pipelinePredicateOperator = (operator: string): string =>
  PREDICATE_OPERATOR_TO_PIPELINE[operator] ?? operator;

const predicateValue = (operator: string, value: unknown): unknown => {
  if (['gt', 'gte', 'lt', 'lte', 'absGt'].includes(operator)) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return value;
};

const operandExpression = (
  value: unknown,
  operator: string,
): AnalysisJsonObject => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { lit: null };
  }
  const operand = value as {
    kind?: unknown;
    value?: unknown;
    ifBlank?: unknown;
  };
  if (operand.kind === 'column') {
    const column: AnalysisJsonObject = { col: String(operand.value ?? '') };
    // A per-operand blank fallback is the same first-non-empty shape the
    // `otherwise` operator compiles to, applied to one operand rather than the
    // whole formula.
    if (operand.ifBlank === undefined || operand.ifBlank === null)
      return column;
    const fallback =
      operator === 'concat' || operator === 'coalesce'
        ? (operand.ifBlank as string | number)
        : normalizeAnalysisNumericLiteral(operand.ifBlank);
    if (fallback === null) {
      throw new Error('A blank fallback in arithmetic must be a finite number');
    }
    return compileFirstNonEmptyExpression([column, { lit: fallback }]);
  }
  const raw = operand.value ?? null;
  if (operator !== 'concat' && operator !== 'coalesce') {
    const numeric = normalizeAnalysisNumericLiteral(raw);
    if (numeric === null) {
      throw new Error('Arithmetic literals require a finite numeric value');
    }
    return { lit: numeric };
  }
  return { lit: raw as string | number | boolean | null };
};

export const compileFirstNonEmptyExpression = (
  values: AnalysisJsonObject[],
): AnalysisJsonObject =>
  values.reduceRight<AnalysisJsonObject>(
    (fallback, value) => ({
      case: [
        {
          when: { expr: value, op: 'non_empty' },
          then: value,
        },
      ],
      else: fallback,
    }),
    { lit: null },
  );

/**
 * Division that answers null instead of failing on a zero denominator. Cypher
 * raises on integer division by zero, and `calculateValue` already returns null
 * for the same row, so an unguarded `/` would make the browser preview and the
 * executed column disagree.
 *
 * A literal denominator needs no guard: a literal zero is rejected while
 * authoring, and every other literal is safe. A plain column guards on the leaf
 * predicate shape; a column carrying a blank fallback is already a `case`, so
 * its effective value is guarded through the expression predicate form.
 */
const compileGuardedDivision = (
  numerator: AnalysisJsonObject,
  denominator: AnalysisJsonObject,
): AnalysisJsonObject => {
  const division = { op: '/', args: [numerator, denominator] };
  if (denominator.lit !== undefined) return division;
  const guard: AnalysisJsonObject =
    typeof denominator.col === 'string'
      ? { col: denominator.col, op: '!=', value: 0 }
      : { expr: denominator, op: '!=', value: 0 };
  return {
    case: [{ when: guard, then: division }],
    else: { lit: null },
  };
};

/**
 * Why a calc step's literals cannot compile, or `null` when all of them can.
 *
 * `operandExpression` throws on a non-finite arithmetic literal or blank
 * fallback, which is the right contract for a direct caller. It is the wrong one
 * for a stored recipe: the compiler runs unguarded inside the render and
 * publication memos, so a single bad literal in a saved view would abort
 * rendering instead of reporting itself through `blockers` like every other
 * unusable step. Screening the step out keeps the throw as an internal
 * invariant while the author sees a sentence they can act on.
 */
const invalidCalcLiteralReason = (step: AnalysisRecipeStep): string | null => {
  if (
    step.config.flagCondition &&
    typeof step.config.flagCondition === 'object' &&
    !Array.isArray(step.config.flagCondition)
  ) {
    return null;
  }
  const operator = String(step.config.operator ?? 'add');
  // Text operators accept any literal, exactly as `operandExpression` does.
  if (operator === 'concat' || operator === 'coalesce') return null;
  const operands = Array.isArray(step.config.operands)
    ? step.config.operands
    : [step.config.left, step.config.right];
  const label =
    typeof step.config.outputColumn === 'string' && step.config.outputColumn
      ? step.config.outputColumn
      : step.outputColumns[0];
  for (const value of operands) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
    const operand = value as {
      kind?: unknown;
      value?: unknown;
      ifBlank?: unknown;
    };
    if (operand.kind === 'column') {
      if (operand.ifBlank === undefined || operand.ifBlank === null) continue;
      if (normalizeAnalysisNumericLiteral(operand.ifBlank) === null) {
        return `“${label}” has a blank fallback that is not a number; enter a number or clear it`;
      }
      continue;
    }
    if (normalizeAnalysisNumericLiteral(operand.value ?? null) === null) {
      return `“${label}” uses a value that is not a number; enter a number`;
    }
  }
  return null;
};

const compileCalcExpression = (
  step: AnalysisRecipeStep,
): AnalysisJsonObject => {
  if (
    step.config.flagCondition &&
    typeof step.config.flagCondition === 'object' &&
    !Array.isArray(step.config.flagCondition)
  ) {
    return compileConditionFlagExpression(
      conditionGroupFromJson(step.config.flagCondition),
      analysisFlagLabels(step.config),
    );
  }
  const operator = String(step.config.operator ?? 'add');
  const operands = Array.isArray(step.config.operands)
    ? step.config.operands
    : [step.config.left, step.config.right];
  const args = operands.map((operand) => operandExpression(operand, operator));
  const foldBinary = (
    values: AnalysisJsonObject[],
    expression: (
      left: AnalysisJsonObject,
      right: AnalysisJsonObject,
    ) => AnalysisJsonObject,
  ): AnalysisJsonObject =>
    values
      .slice(1)
      .reduce(
        (left, right) => expression(left, right),
        values[0] ?? { lit: null },
      );
  if (operator === 'pctOf') {
    return {
      op: '*',
      args: [
        compileGuardedDivision(
          args[0] ?? { lit: null },
          args[1] ?? { lit: null },
        ),
        { lit: 100 },
      ],
    };
  }
  if (operator === 'divide') {
    return compileGuardedDivision(
      args[0] ?? { lit: null },
      args[1] ?? { lit: null },
    );
  }
  if (operator === 'coalesce') {
    return compileFirstNonEmptyExpression(args);
  }
  if (operator === 'concat') {
    // A named function, not an overloaded `+`. Arithmetic `+` over two strings
    // returns a null column on the engine — silently, which is why text-join was
    // gated at all — while `concat` is null-safe per operand and yields the
    // joined string. It takes all operands at once rather than folding pairwise.
    return { fn: 'concat', args };
  }
  if (operator === 'add' && args.length > 2) {
    return foldBinary(args, (left, right) => ({
      op: '+',
      args: [left, right],
    }));
  }
  return {
    op: CALC_OPERATOR_TO_PIPELINE[operator] ?? operator,
    args,
  };
};

type PredicateLeaf = Record<string, unknown>;
type PredicateCnf = PredicateLeaf[][];

const compileConditionLeaf = (
  node: ConditionLeaf,
  lhs: 'col' | 'field' = 'col',
  nodeAlias?: string,
): PredicateLeaf => {
  const op = pipelinePredicateOperator(node.operator);
  const value =
    node.operator === 'isOneOf'
      ? node.value
          .split(',')
          .map((entry) => entry.trim())
          .filter(Boolean)
      : predicateValue(node.operator, node.value);
  if (node.operator === 'absGt') {
    return {
      expr: {
        fn: 'abs',
        args: [
          {
            [lhs]: node.field,
            ...(nodeAlias ? { node: nodeAlias } : {}),
          },
        ],
      },
      op: '>',
      value,
    };
  }
  return {
    [lhs]: node.field,
    ...(nodeAlias ? { node: nodeAlias } : {}),
    op,
    ...(op === 'missing' || op === 'non_empty' ? {} : { value }),
  };
};

const conditionToCnf = (
  node: ConditionNode,
  lhs: 'col' | 'field' = 'col',
  nodeAlias?: string,
): PredicateCnf => {
  if (node.type === 'leaf') {
    return [[compileConditionLeaf(node, lhs, nodeAlias)]];
  }
  if (node.type === 'related') return [];
  const childCnfs = node.conditions.map((child) =>
    conditionToCnf(child, lhs, nodeAlias),
  );
  if (node.joiner === 'all') return childCnfs.flat();
  return childCnfs.reduce<PredicateCnf>((left, right) => {
    if (left.length === 0) return right;
    if (right.length === 0) return left;
    return left.flatMap((leftClause) =>
      right.map((rightClause) => [...leftClause, ...rightClause]),
    );
  }, []);
};

const compileConditionPredicates = (
  group: ConditionGroup,
  lhs: 'col' | 'field' = 'col',
  nodeAlias?: string,
): PredicateLeaf[] =>
  conditionToCnf(group, lhs, nodeAlias).map((clause) =>
    clause.length === 1 ? clause[0] : { op: 'or', conditions: clause },
  );

/**
 * Compiles a condition tree into a Yes/No `case` expression.
 *
 * `filter_rows.predicates` accepts a composite `{op:'and'|'or', conditions}`
 * group, but a `case` `when` does not: the deployed engine answers
 * `has unknown key(s) ['conditions']; allowed: ['col','expr','field','left',
 * 'node','op','param','right','transform','value']`. The grammar there is a
 * single leaf. So boolean structure is expressed by nesting instead:
 *
 *   A and B  →  case when A then (B's own Yes/No) else 'No'
 *   A or B   →  case when A then 'Yes' else (B's own Yes/No)
 *
 * A nested group becomes an `expr` predicate over its own Yes/No result, which
 * is the same expression-predicate form the division guard uses. Both nesting
 * and `expr` predicates are probe-proven; composite `when` groups are not, and
 * cannot be.
 */
export const compileConditionFlagExpression = (
  node: ConditionNode,
  labels: AnalysisFlagLabels = ANALYSIS_FLAG_DEFAULT_LABELS,
): AnalysisJsonObject => {
  if (node.type === 'related') return { lit: labels.false };
  if (node.type === 'leaf') {
    return {
      case: [
        {
          when: compileConditionLeaf(node) as AnalysisJsonObject,
          then: { lit: labels.true },
        },
      ],
      else: { lit: labels.false },
    };
  }
  // Relationship conditions have no row-level expression, so a flag ignores
  // them rather than compiling a predicate the engine would reject.
  const children = node.conditions.filter((child) => child.type !== 'related');
  if (children.length === 0) return { lit: labels.false };
  if (children.length === 1)
    return compileConditionFlagExpression(children[0], labels);
  const [head, ...rest] = children;
  const headPredicate: AnalysisJsonObject =
    head.type === 'leaf'
      ? (compileConditionLeaf(head) as AnalysisJsonObject)
      : {
          expr: compileConditionFlagExpression(head, labels),
          op: '=',
          // Compared against the label the child was JUST compiled with, not
          // the word "Yes". A nested group emits `labels.true` on a match, so
          // a hard-coded sentinel here would never equal it once the author
          // renames the branch, and every nested all/any would read false.
          value: labels.true,
        };
  const restExpression = compileConditionFlagExpression(
    { ...node, conditions: rest },
    labels,
  );
  return node.joiner === 'all'
    ? {
        case: [{ when: headPredicate, then: restExpression }],
        else: { lit: labels.false },
      }
    : {
        case: [{ when: headPredicate, then: { lit: labels.true } }],
        else: restExpression,
      };
};

/** Applied controls filter like keep conditions, but bind a run param (C7). */
const appliedControlPredicates = (
  sheet: AnalysisSheetRecipe,
): PredicateLeaf[] =>
  sheet.controls
    .filter((control) => control.applied)
    .map((control) => {
      const op = pipelinePredicateOperator(control.operator);
      return {
        col: control.field,
        op,
        ...(op === 'missing' || op === 'non_empty'
          ? {}
          : { param: control.paramName }),
      };
    });

/** Columns a predicate reads, including `or` clauses and `abs` expressions. */
const predicateColumns = (predicate: PredicateLeaf): string[] => {
  if (Array.isArray(predicate.conditions)) {
    return (predicate.conditions as PredicateLeaf[]).flatMap(predicateColumns);
  }
  if (typeof predicate.col === 'string') return [predicate.col];
  const expr = predicate.expr as { args?: unknown } | undefined;
  const args = Array.isArray(expr?.args) ? (expr.args as PredicateLeaf[]) : [];
  return args.flatMap((arg) => (typeof arg.col === 'string' ? [arg.col] : []));
};

const relatedConditionCount = (node: ConditionNode): number =>
  node.type === 'related'
    ? 1
    : node.type === 'group'
      ? node.conditions.reduce(
          (total, child) => total + relatedConditionCount(child),
          0,
        )
      : 0;

type ConditionGrain = 'source' | 'made' | 'related';

const conditionGrains = (
  node: ConditionNode,
  madeFields: Set<string>,
): Set<ConditionGrain> => {
  if (node.type === 'related') return new Set(['related']);
  if (node.type === 'leaf') {
    return new Set([
      madeFields.has(node.field) || isAnalysisGeneratedField(node.field)
        ? 'made'
        : 'source',
    ]);
  }
  return node.conditions.reduce<Set<ConditionGrain>>((grains, child) => {
    conditionGrains(child, madeFields).forEach((grain) => grains.add(grain));
    return grains;
  }, new Set());
};

const conditionGroupFor = (
  template: ConditionGroup,
  conditions: ConditionNode[],
): ConditionGroup => ({ ...template, joiner: template.joiner, conditions });

const partitionConditionNode = (
  node: ConditionNode,
  madeFields: Set<string>,
): {
  partitions: Record<ConditionGrain, ConditionNode[]>;
  unsupported: boolean;
} => {
  const partitions: Record<ConditionGrain, ConditionNode[]> = {
    source: [],
    made: [],
    related: [],
  };
  const grains = conditionGrains(node, madeFields);
  if (grains.size === 1) {
    partitions[[...grains][0] as ConditionGrain].push(node);
    return { partitions, unsupported: false };
  }
  if (node.type !== 'group' || node.joiner === 'any') {
    return { partitions, unsupported: grains.size > 1 };
  }

  let unsupported = false;
  node.conditions.forEach((condition) => {
    const child = partitionConditionNode(condition, madeFields);
    unsupported ||= child.unsupported;
    (Object.keys(partitions) as ConditionGrain[]).forEach((grain) => {
      partitions[grain].push(...child.partitions[grain]);
    });
  });
  return { partitions, unsupported };
};

const partitionConditionGroup = (
  group: ConditionGroup,
  madeFields: Set<string>,
): {
  source: ConditionGroup;
  made: ConditionGroup;
  related: ConditionGroup;
  unsupported: boolean;
} => {
  const partitions: Record<ConditionGrain, ConditionNode[]> = {
    source: [],
    made: [],
    related: [],
  };
  const overallGrains = conditionGrains(group, madeFields);
  if (group.joiner === 'any' && overallGrains.size > 1) {
    return {
      source: conditionGroupFor(group, []),
      made: conditionGroupFor(group, []),
      related: conditionGroupFor(group, []),
      unsupported: true,
    };
  }
  let unsupported = false;
  group.conditions.forEach((condition) => {
    const child = partitionConditionNode(condition, madeFields);
    unsupported ||= child.unsupported;
    (Object.keys(partitions) as ConditionGrain[]).forEach((grain) => {
      partitions[grain].push(...child.partitions[grain]);
    });
  });
  return {
    source: conditionGroupFor(group, partitions.source),
    made: conditionGroupFor(group, partitions.made),
    related: conditionGroupFor(group, partitions.related),
    unsupported,
  };
};

const predicateReadsMadeField = (
  predicate: PredicateLeaf,
  madeFields: Set<string>,
): boolean =>
  predicateColumns(predicate).some(
    (field) => madeFields.has(field) || isAnalysisGeneratedField(field),
  );

const activeFilterPredicates = (
  filters: DataTableFilterMeta | undefined,
): Record<string, unknown>[] =>
  Object.entries(filters ?? {}).flatMap(([field, raw]) => {
    const typed = raw as {
      constraints?: Array<{ value?: unknown; matchMode?: string }>;
      value?: unknown;
      matchMode?: string;
    };
    const constraints = typed.constraints ?? [typed];
    return constraints.flatMap((constraint) =>
      constraint.value === null ||
      constraint.value === undefined ||
      String(constraint.value).trim() === ''
        ? []
        : [
            {
              col: field,
              op: pipelinePredicateOperator(constraint.matchMode ?? 'contains'),
              value: predicateValue(
                constraint.matchMode ?? 'contains',
                constraint.value,
              ),
            },
          ],
    );
  });

const pushFilterRows = (
  steps: PipelineStep[],
  from: string,
  predicates: Record<string, unknown>[],
  as: string,
): string => {
  if (predicates.length === 0) return from;
  steps.push({
    op: 'filter_rows',
    from,
    args: { predicates },
    as,
  });
  return as;
};

/**
 * Compiles one sheet into semantic RowSet order (C4):
 * source → Narrowed (table filters + related existence) → Made matches/group →
 * derivations → Kept (field keep-filter + applied controls) → sort/projection.
 *
 * `executable` is capability-gated: a recipe that needs a contract without
 * captured evidence still compiles for inspection but must not replace the
 * honest browser preview.
 */
export function compileAnalysisRecipe(
  sheet: AnalysisSheetRecipe,
  options: CompileAnalysisRecipeOptions,
): CompiledAnalysisRecipe {
  const capabilities = options.capabilities ?? UNVERIFIED_ANALYSIS_CAPABILITIES;
  if (!sheet.source.label) {
    return {
      pipeline: {
        name: `analysis_sheet_${sheet.id.replace(/[^a-zA-Z0-9_-]/g, '_')}`,
        steps: [],
        emit: 'last',
      },
      executable: false,
      missingCapabilities: ['toTable'],
      blockers: [],
      outputColumns: [],
      rowKey: '__analysis_row_key',
      columnLabels: {},
    };
  }

  const madeFields = new Set(sheet.steps.flatMap((step) => step.outputColumns));
  // The computed columns read as the sheet heads them; the internal ones are
  // labelled where they are minted below.
  const columnLabels: Record<string, string> = {
    __analysis_row_key: 'record ID',
    ...analysisColumnLabels(buildAnalysisColumnDisplay(sheet)),
  };
  const keepTree = conditionGroupFromJson(sheet.keepFilter);
  const partitionedKeep = partitionConditionGroup(keepTree, madeFields);
  const relatedConditions = relatedLeavesOf(partitionedKeep.related);
  const relatedCount = relatedConditionCount(partitionedKeep.related);
  const canCompileRelatedConditions =
    partitionedKeep.related.joiner === 'all' &&
    relatedConditions.length === relatedCount &&
    relatedConditions.every(({ leaf }) => Boolean(leaf.relationship));

  // A search-sourced sheet seeds from the fulltext index instead of the whole
  // record type, so its rows are the search's hits and they refresh with the
  // query. Scoped to the sheet's one label, which is also what makes the
  // relevance columns available at all.
  const searchSource = sheet.source.search;
  /**
   * The caller's columns minus the relevance ones.
   *
   * Relevance is a backend projection, not a node property, so anywhere the
   * source columns are read AS node fields — the rollup's `group_rows` keys —
   * they would compile to `{node:'n', field:'search_score'}` and read a property
   * no node has. And anywhere they are a promise of what the sink projects, they
   * would re-make the claim the explicit branch below is careful to withhold.
   * The grid puts relevance in `sourceColumns` for a search sheet, so this is
   * reachable, not theoretical.
   */
  const sourceColumnsWithoutRelevance = options.sourceColumns.filter(
    (field) => !(SEARCH_RELEVANCE_COLUMNS as readonly string[]).includes(field),
  );
  if (searchSource?.mode === 'semantic') {
    // There is no pipeline op for a meaning search, so there is no step that
    // means it. Compiling it as `search` would run a keyword search under a
    // chip that says "meaning", which is worse than refusing.
    return {
      pipeline: {
        name: `analysis_sheet_${sheet.id.replace(/[^a-zA-Z0-9_-]/g, '_')}`,
        steps: [],
        emit: 'last',
      },
      executable: false,
      missingCapabilities: ['semanticSearchSource'],
      blockers: [],
      outputColumns: [],
      rowKey: '__analysis_row_key',
      columnLabels: {},
    };
  }
  const steps: PipelineStep[] = [
    searchSource
      ? {
          op: 'search',
          args: {
            query: searchSource.query,
            label: sheet.source.label,
            // Without an explicit limit the backend falls back to 10, which
            // would silently cap the sheet at ten rows.
            limit: DEFAULT_REMAP_SEARCH_LIMIT,
            ...(searchSource.contains ? { contains: true } : {}),
            ...(searchSource.filters?.length
              ? { filters: searchSource.filters }
              : {}),
          },
          as: 'source_nodes',
        }
      : {
          op: 'select_label',
          args: { label: sheet.source.label },
          as: 'source_nodes',
        },
  ];
  let sourceNodes = 'source_nodes';
  if (canCompileRelatedConditions) {
    relatedConditions.forEach(({ leaf }, index) => {
      const as = `related_filter_${index}`;
      steps.push({
        op: 'filter_by_relationship',
        from: sourceNodes,
        args: {
          rel: leaf.relationship,
          target_label: leaf.recordLabel,
          direction: leaf.direction ?? 'both',
          exists: leaf.quantifier === 'has',
          ...(leaf.where?.conditions.length
            ? {
                target_predicates: compileConditionPredicates(
                  leaf.where,
                  'field',
                ),
              }
            : {}),
        },
        as,
      });
      sourceNodes = as;
    });
  }
  // A value join is the sheet's ROW SOURCE, not another step on top of one.
  // `join_on_value` takes two NodeSet HANDLES rather than a `from` chain and
  // re-matches by id itself, so it cannot extend the relationship chain the way
  // `match_related` does — it has to replace `rows_from_nodes`.
  //
  // It pairs rows, so a to-many match would fan the sheet out. Regrouping on
  // `left_id` immediately after puts it back to one row per source record, and
  // — the reason this stays tractable — names every column exactly as the
  // relationship path names it, so the whole tail below is untouched.
  const joinSteps = sheet.steps.filter((step) => step.kind === 'join');
  const joinStep = analysisJoinStep(sheet.steps);
  const joinConfig = joinStep ? normalizeAnalysisJoinConfig(joinStep) : null;
  const joinSourceColumns = [...new Set(sourceColumnsWithoutRelevance)].filter(
    (field) => field !== '__analysis_row_key',
  );
  if (joinConfig) {
    const rightHandle = 'join_right';
    steps.push({
      op: 'select_label',
      args: { label: joinConfig.targetLabel },
      as: rightHandle,
    });
    steps.push({
      op: 'join_on_value',
      handles: [sourceNodes, rightHandle],
      args: {
        left_field: joinConfig.sourceField,
        right_field: joinConfig.targetField,
        left_fields: [...new Set([joinConfig.sourceField, ...joinSourceColumns])],
        right_fields: [
          ...new Set(joinConfig.outputs.map((output) => output.field)),
        ],
      },
      as: 'joined_rows',
    });
    steps.push({
      op: 'group_rows',
      from: 'joined_rows',
      args: {
        keys: [
          { col: 'left_id', as: '__analysis_row_key' },
          ...joinSourceColumns.map((field) => ({
            col: `left_${field}`,
            as: field,
          })),
        ],
        // `first`, matching the relationship lookup's own choice: one value per
        // source record, chosen arbitrarily when the match is to-many. The
        // picker says so before the step is accepted.
        aggregations: joinConfig.outputs.map((output) => ({
          name: output.outputColumn,
          op: 'first',
          col: `right_${output.field}`,
        })),
      },
      as: 'joined_grain',
    });
    columnLabels.left_id = columnLabels.__analysis_row_key;
    joinSourceColumns.forEach((field) => {
      columnLabels[`left_${field}`] = field;
    });
    joinConfig.outputs.forEach((output) => {
      columnLabels[`right_${output.field}`] = columnLabels[output.outputColumn];
    });
  } else {
    steps.push({
      op: 'rows_from_nodes',
      from: sourceNodes,
      args: {
        fields: [
          { field: 'id', as: '__analysis_row_key' },
          // Relevance is readable ONLY off the search step's own projection, and
          // only as bare strings — the object form is ignored by the backend.
          ...(searchSource && sourceNodes === 'source_nodes'
            ? SEARCH_RELEVANCE_COLUMNS
            : []),
          ...joinSourceColumns,
        ],
      },
      as: 'source_rows',
    });
  }
  let current = joinConfig ? 'joined_grain' : 'source_rows';

  const tablePredicates = activeFilterPredicates(options.filters);
  const sourceTablePredicates = tablePredicates.filter(
    (predicate) => !predicateReadsMadeField(predicate, madeFields),
  );
  const madeTablePredicates = tablePredicates.filter((predicate) =>
    predicateReadsMadeField(predicate, madeFields),
  );
  const controlPredicates = appliedControlPredicates(sheet);
  const sourceControlPredicates = controlPredicates.filter(
    (predicate) => !predicateReadsMadeField(predicate, madeFields),
  );
  const madeControlPredicates = controlPredicates.filter((predicate) =>
    predicateReadsMadeField(predicate, madeFields),
  );
  const sourceRecipePredicates = compileConditionPredicates(
    partitionedKeep.source,
  );
  const madeRecipePredicates = compileConditionPredicates(partitionedKeep.made);
  const narrowedPredicates = [
    ...sourceTablePredicates,
    ...sourceControlPredicates,
    ...sourceRecipePredicates,
  ];
  current = pushFilterRows(steps, current, narrowedPredicates, 'narrowed');

  const madeSteps = sheet.steps.filter(
    (step) => step.kind === 'lookup' || step.kind === 'rollup',
  );
  const rollupMetricsByStep = new Map(
    madeSteps.flatMap((step) =>
      step.kind === 'rollup'
        ? [
            [
              step,
              compiledRollupMetrics(normalizeAnalysisRollupMetrics(step)),
            ] as const,
          ]
        : [],
    ),
  );
  const aggregationSpecs: Record<string, unknown>[] = [];
  const correctionDerivations: Record<string, unknown>[] = [];
  const pathGroups = new Map<
    string,
    {
      hops: ReturnType<typeof normalizeAnalysisLookupConfig>['path']['hops'];
      steps: AnalysisRecipeStep[];
      aliases: string[];
      cardinalityColumn: string;
    }
  >();
  madeSteps.forEach((step) => {
    const hops = normalizeAnalysisLookupConfig(step).path.hops;
    const pathKey = analysisRelationshipPathId({ hops });
    const existing = pathGroups.get(pathKey);
    if (existing) {
      existing.steps.push(step);
      return;
    }
    pathGroups.set(pathKey, {
      hops,
      steps: [step],
      aliases: [],
      cardinalityColumn: `__analysis_path_cardinality_${pathGroups.size}`,
    });
  });
  const distinctPathCount = pathGroups.size;
  const needsCrossPathCorrection =
    distinctPathCount > 1 &&
    madeSteps.some(
      (step) =>
        step.kind === 'rollup' &&
        (rollupMetricsByStep.get(step) ?? []).some(
          (metric) => metric.metric === 'count' || metric.metric === 'sum',
        ),
    );
  const sharedPrefixAliases = new Map<string, string>();
  let aliasIndex = 0;

  pathGroups.forEach((pathGroup, pathKey) => {
    pathGroup.hops.forEach((hop, hopIndex) => {
      const prefixKey = analysisRelationshipPathId({
        hops: pathGroup.hops.slice(0, hopIndex + 1),
      });
      const cacheKey = needsCrossPathCorrection
        ? `${pathKey}::${prefixKey}`
        : prefixKey;
      const cached = sharedPrefixAliases.get(cacheKey);
      if (cached) {
        pathGroup.aliases.push(cached);
        return;
      }
      const parentPrefixKey = analysisRelationshipPathId({
        hops: pathGroup.hops.slice(0, hopIndex),
      });
      const parentCacheKey = needsCrossPathCorrection
        ? `${pathKey}::${parentPrefixKey}`
        : parentPrefixKey;
      const parentAlias = sharedPrefixAliases.get(parentCacheKey);
      const targetAlias = `related_path_${aliasIndex}`;
      const matched = `matched_path_${aliasIndex}`;
      aliasIndex += 1;
      steps.push({
        op: 'match_related',
        from: current,
        args: {
          from_alias: parentAlias ?? 'n',
          rel: hop.relationship,
          direction: hop.direction,
          target_label: hop.targetLabel,
          target_alias: targetAlias,
          optional: true,
        },
        as: matched,
      });
      current = matched;
      sharedPrefixAliases.set(cacheKey, targetAlias);
      pathGroup.aliases.push(targetAlias);
    });
  });

  const safeCardinality = (column: string): Record<string, unknown> => ({
    case: [
      {
        when: { col: column, op: '>', value: 0 },
        then: { col: column },
      },
    ],
    else: { lit: 1 },
  });
  const crossPathFactor = (pathKey: string): Record<string, unknown> =>
    [...pathGroups.entries()]
      .filter(([candidateKey]) => candidateKey !== pathKey)
      .map(([, group]) => safeCardinality(group.cardinalityColumn))
      .reduce<Record<string, unknown>>(
        (left, right) => ({ op: '*', args: [left, right] }),
        { lit: 1 },
      );

  pathGroups.forEach((pathGroup, pathKey) => {
    const targetAlias =
      pathGroup.aliases[Math.max(0, pathGroup.hops.length - 1)];
    if (needsCrossPathCorrection) {
      aggregationSpecs.push({
        name: pathGroup.cardinalityColumn,
        op: 'count_distinct',
        node: targetAlias,
        field: 'id',
      });
      const terminal = pathGroup.hops[pathGroup.hops.length - 1]?.targetLabel;
      columnLabels[pathGroup.cardinalityColumn] = terminal
        ? `number of related ${terminal} records`
        : 'number of related records';
    }
    pathGroup.steps.forEach((step, stepIndex) => {
      if (step.kind === 'lookup') {
        normalizedLookupOutputs(
          step,
          Math.max(0, pathGroup.hops.length - 1),
        ).forEach((output) => {
          const alias = pathGroup.aliases[output.hopIndex];
          // With a fallback the aggregate lands in a raw column and the public
          // one is derived from it, first-non-empty. Same shape the calc
          // operand's `ifBlank` and the `otherwise` operator compile to, and the
          // same raw-then-derived pattern the cross-path correction already uses
          // — including reusing its `derive_rows`, which runs immediately after
          // this group, the one position a coalesce over an aggregate can sit.
          const hasFallback = output.ifBlank !== null;
          const aggregationName = hasFallback
            ? `__analysis_lookup_raw_${output.outputColumn}`
            : output.outputColumn;
          // Direction *is* the op: `first` takes the minimum of the ordering
          // field, `last` the maximum. There is no asc/desc alongside it. No
          // `tie_break` either — the backend already breaks ties on the related
          // record's `id`, so naming one here would only risk disagreeing with
          // it. Without a rule this stays a bare `first`, which is the arbitrary
          // pick every saved step compiled to before ordering existed.
          // Only the terminal hop. `orderBy.field` is a property of the
          // *terminal* label — the authoring form builds its field list from
          // `selectedPath.terminalLabel` — so pointing it at an intermediate
          // alias would order by a property that node does not have. An
          // intermediate output is an identity column passed through on the way,
          // and keeps the unordered `first` it has always compiled to.
          // Only the terminal hop. `orderBy.field` is a property of the
          // *terminal* label — the authoring form builds its field list from
          // `selectedPath.terminalLabel` — so pointing it at an intermediate
          // alias would order by a property that node does not have. An
          // intermediate output is an identity column passed through on the way,
          // and keeps the unordered `first` it has always compiled to.
          const orderBy =
            output.hopIndex === Math.max(0, pathGroup.hops.length - 1)
              ? lookupOrderBy(step)
              : null;
          aggregationSpecs.push({
            name: aggregationName,
            op: orderBy?.direction ?? 'first',
            node: alias,
            field: output.field,
            ...(orderBy
              ? {
                  order_by: {
                    node: alias,
                    field: orderBy.field,
                    ...(orderBy.type ? { type: orderBy.type } : {}),
                  },
                }
              : {}),
            ...(output.condition?.conditions.length
              ? {
                  where: compileConditionPredicates(
                    output.condition,
                    'field',
                    alias,
                  ),
                }
              : {}),
          });
          if (hasFallback) {
            columnLabels[aggregationName] = columnLabels[output.outputColumn];
            correctionDerivations.push({
              name: output.outputColumn,
              expr: compileFirstNonEmptyExpression([
                { col: aggregationName },
                { lit: output.ifBlank as string | number },
              ]),
            });
          }
        });
        return;
      }
      (rollupMetricsByStep.get(step) ?? []).forEach((metric, metricIndex) => {
        const needsCorrection =
          needsCrossPathCorrection &&
          (metric.metric === 'count' || metric.metric === 'sum');
        const aggregationName = needsCorrection
          ? `__analysis_path_raw_${pathGroup.cardinalityColumn}_${stepIndex}_${metricIndex}`
          : metric.outputColumn;
        aggregationSpecs.push({
          name: aggregationName,
          op: metric.metric,
          node: targetAlias,
          ...(metric.field ? { field: metric.field } : {}),
          ...(metric.condition?.conditions.length
            ? {
                where: compileConditionPredicates(
                  metric.condition,
                  'field',
                  targetAlias,
                ),
              }
            : {}),
        });
        if (needsCorrection) {
          columnLabels[aggregationName] = columnLabels[metric.outputColumn];
          correctionDerivations.push({
            name: metric.outputColumn,
            expr: {
              op: '/',
              args: [{ col: aggregationName }, crossPathFactor(pathKey)],
            },
          });
        }
      });
    });
  });

  if (aggregationSpecs.length > 0) {
    steps.push({
      op: 'group_rows',
      from: current,
      args: {
        keys: [
          { node: 'n', field: 'id', as: '__analysis_row_key' },
          ...sourceColumnsWithoutRelevance
            .filter((field) => field !== '__analysis_row_key')
            .map((field) => ({ node: 'n', field, as: field })),
        ],
        aggregations: aggregationSpecs,
      },
      as: 'grain_preserved',
    });
    current = 'grain_preserved';
  }
  if (correctionDerivations.length > 0) {
    steps.push({
      op: 'derive_rows',
      from: current,
      args: { derivations: correctionDerivations },
      as: 'path_metrics_corrected',
    });
    current = 'path_metrics_corrected';
  }

  const calcSteps = sheet.steps.filter((step) => step.kind === 'calc');
  const { ordered: orderedCalcSteps, unresolved: unresolvedCalcSteps } =
    orderAnalysisCalcSteps(calcSteps);
  // Screened before compiling, so an unusable literal reports through `blockers`
  // rather than throwing out of the compiler and taking the render with it.
  const calcLiteralBlockers: string[] = [];
  const compilableCalcSteps = orderedCalcSteps.filter((step) => {
    const reason = invalidCalcLiteralReason(step);
    if (reason) calcLiteralBlockers.push(reason);
    return reason === null;
  });
  compilableCalcSteps.forEach((step, index) => {
    const as = `derived_${index}`;
    steps.push({
      op: 'derive_rows',
      from: current,
      args: {
        derivations: [
          {
            name: step.outputColumns[0],
            expr: compileCalcExpression(step),
          },
        ],
      },
      as,
    });
    current = as;
  });

  // Made and generated-field conditions cannot run until their columns exist.
  const keptPredicates: Record<string, unknown>[] = [
    ...madeTablePredicates,
    ...madeRecipePredicates,
    ...madeControlPredicates,
  ];
  current = pushFilterRows(steps, current, keptPredicates, 'filtered');

  const outputColumns = [
    '__analysis_row_key',
    // The relevance columns are projected by the search step above but are not
    // anyone's `sourceColumns`, so without naming them here they fall out of
    // everything keyed off this list — and `sinkOrderBy` treats a sort over an
    // unprojected column as unsortable, so clicking sort on Relevance, the
    // column the sheet exists for, silently did nothing.
    //
    // Claimed only when the rows reach the sink still carrying them:
    //   - `sourceNodes === 'source_nodes'`, because relevance is readable only
    //     off the search step's own `rows_from_nodes`;
    //   - and no value join, which REPLACES `rows_from_nodes` with
    //     `join_on_value` and so never projects relevance at all. Without this
    //     the sink emitted `order_by: search_score` over a column the pipeline
    //     does not have — the failure the paragraph below describes, reached by
    //     a route that did not exist when it was written;
    //   - and no rollup, because the `grain_preserved` `group_rows` keeps only
    //     its keys, which are `{node, field}` reads of real node properties.
    //     Relevance is not a node property, so it cannot be added there — it is
    //     genuinely gone past that stage.
    //
    // Advertising it anyway would be worse than the silent no-op it replaced:
    // the sink would emit `order_by: search_score` over a column the pipeline
    // no longer has, and the backend rejects the whole run — "Execution failed"
    // and no rows, instead of merely unsorted ones.
    ...(searchSource &&
    sourceNodes === 'source_nodes' &&
    !joinConfig &&
    aggregationSpecs.length === 0
      ? SEARCH_RELEVANCE_COLUMNS
      : []),
    ...sourceColumnsWithoutRelevance,
    ...sheet.steps.flatMap((step) => step.outputColumns),
  ];
  if (correctionDerivations.length > 0) {
    const projectedColumns = [...new Set(outputColumns)];
    steps.push({
      op: 'group_rows',
      from: current,
      args: {
        keys: projectedColumns.map((column) => ({ col: column, as: column })),
        aggregations: [],
      },
      as: 'path_metrics_projected',
    });
    current = 'path_metrics_projected';
  }
  // Guarded by what this sink actually projects, same as the breakdown path.
  // Reachable in one click and confirmed live: sorting a Made sheet by the grid's
  // `displayId` column emitted `order_by: displayId:asc`, which the sink does not
  // project, and the backend rejected the whole run — the sheet showed "Execution
  // failed" and no rows until the sort was cleared.
  const orderBy = sinkOrderBy(options, '__analysis_row_key', outputColumns);
  steps.push({
    op: 'to_table',
    from: current,
    args: {
      ...(orderBy ? { order_by: orderBy } : {}),
      offset: options.target === 'publication' ? 0 : (options.offset ?? 0),
      ...(options.limit !== undefined ? { limit: options.limit } : {}),
      ...sinkIncludeTotal(options),
    },
    as: 'out',
  });

  const required = new Set<keyof AnalysisBackendCapabilities>(['toTable']);
  if (madeSteps.length) {
    required.add('matchRelated');
    required.add('groupRows');
  }
  // Calc steps are not the only source of a `derive_rows`: the cross-path
  // correction and a lookup's blank fallback both emit one too. Declaring only
  // for calc steps under-reported the requirement, so a sheet using either could
  // claim to be runnable against a backend without `derive_rows`.
  if (calcSteps.length || correctionDerivations.length) {
    required.add('deriveRows');
  }
  if (joinStep) {
    required.add('valueJoin');
  }
  if (calcSteps.some((step) => String(step.config.operator) === 'concat')) {
    required.add('concatRows');
  }
  // Only steps that actually carry an ordering rule declare it. A legacy lookup
  // compiles to the same bare `first` it always did, so adopting this capability
  // cannot make an existing sheet stop running.
  if (madeSteps.some((step) => step.kind === 'lookup' && lookupOrderBy(step))) {
    required.add('orderedPick');
  }
  if (
    [...rollupMetricsByStep.values()].some((metrics) =>
      metrics.some((metric) => metric.metric === 'avg'),
    )
  ) {
    required.add('averageRollup');
  }
  if (narrowedPredicates.length > 0 || keptPredicates.length > 0) {
    required.add('filterRows');
  }
  if (relatedCount > 0) required.add('relatedPredicates');
  const missingCapabilities = new Set(
    [...required].filter(
      (capability) =>
        !capabilities[capability] ||
        (capability === 'relatedPredicates' && !canCompileRelatedConditions),
    ),
  );
  if (partitionedKeep.unsupported) missingCapabilities.add('filterRows');
  if (
    relatedConditionCount(keepTree) > relatedCount ||
    (relatedCount > 0 && !canCompileRelatedConditions)
  ) {
    missingCapabilities.add('relatedPredicates');
  }

  const blockers = [
    // Only the FIRST join compiles — the sheet has one row source, and
    // `analysisJoinStep` picks one. A second join step used to pass every gate
    // (no blocker, `executable: true`, no Mocked badge) while its columns
    // rendered headings over blank cells on every row: uncomputed values shown
    // as data, which is the worst shape a defect can take here.
    // A join step whose config never normalized compiles to nothing: the
    // pipeline falls back to `rows_from_nodes` while `outputColumns` still
    // advertises the step's column, so the grid shows a heading over blank
    // cells and the sheet reports itself runnable. Same shape as the
    // second-join case below, reached by a damaged config rather than a
    // second step.
    ...(joinStep && !joinConfig
      ? [
          'This sheet matches records by value, but the match is incomplete — it needs a field on each side and at least one column to add. Rebuild it from Add column → From related data.',
        ]
      : []),
    ...(joinSteps.length > 1
      ? [
          `This sheet already matches records by value (${joinConfig?.targetLabel ?? 'another record type'}), and it can only do that once — the match decides which records the sheet holds. Remove one of the matches, or build the second on a sheet of its own.`,
        ]
      : []),
    // `join_on_value` re-matches by id and hands back a column-only RowSet with
    // no bound node alias, which is exactly what `match_related` needs to
    // traverse from. The two cannot appear on one sheet, and saying so here
    // beats the backend's "from_alias 'n' is not a bound node alias".
    ...(joinStep &&
    sheet.steps.some(
      (step) => step.kind === 'lookup' || step.kind === 'rollup',
    )
      ? [
          'This sheet matches records by value, which replaces the records it started from, so it cannot also follow a relationship. Keep one or the other, or build the relationship columns on a separate sheet.',
        ]
      : []),
    ...unresolvedCalcSteps.map((step) => {
      const label =
        typeof step.config.outputColumn === 'string' && step.config.outputColumn
          ? step.config.outputColumn
          : step.outputColumns[0];
      return `“${label}” reads a calculation that is produced after it, or itself; point it at an earlier column`;
    }),
    ...calcLiteralBlockers,
  ];

  return {
    pipeline: {
      name: `analysis_sheet_${sheet.id.replace(/[^a-zA-Z0-9_-]/g, '_')}`,
      steps,
      emit: 'last',
    },
    executable: missingCapabilities.size === 0 && blockers.length === 0,
    missingCapabilities: [...missingCapabilities],
    blockers,
    outputColumns: [...new Set(outputColumns)],
    rowKey: '__analysis_row_key',
    columnLabels,
  };
}

/**
 * Compiles any sheet, resolving a breakdown's parent from the sheet map. Every
 * caller goes through here so the grid, the exports and a published card cannot
 * disagree about how a grouped sheet is built.
 */
export function compileAnalysisSheet(
  sheet: AnalysisSheetRecipe,
  sheets: Record<string, AnalysisSheetRecipe> | undefined,
  options: CompileAnalysisRecipeOptions,
): CompiledAnalysisRecipe {
  if (sheet.breakdown) {
    const parent = sheets?.[sheet.breakdown.parentSheetId];
    if (parent) return compileAnalysisBreakdown(parent, sheet, options);
  }
  return compileAnalysisRecipe(sheet, options);
}

/**
 * Applied control values for a sheet and every ancestor whose steps its
 * pipeline inlines. A breakdown compiles its parent's recipe into its own
 * pipeline, so a parent control's `param` leaf is part of the child's run and
 * has to be bound there too — sending only the child's controls leaves it
 * unbound and fails the whole run.
 */
export function analysisAppliedControlParams(
  sheet: AnalysisSheetRecipe,
  sheets?: Record<string, AnalysisSheetRecipe>,
): Record<string, PipelineParamValue> {
  const params: Record<string, PipelineParamValue> = {};
  const visited = new Set<string>();
  let current: AnalysisSheetRecipe | undefined = sheet;
  while (current && !visited.has(current.id)) {
    visited.add(current.id);
    current.controls.forEach((control) => {
      if (!control.applied) return;
      // The nearest sheet owns a param name a lineage happens to share.
      if (!(control.paramName in params)) {
        params[control.paramName] = control.value;
      }
    });
    const parentId: string | undefined = current.breakdown?.parentSheetId;
    current = parentId ? sheets?.[parentId] : undefined;
  }
  return params;
}

/** Every control param a sheet's lineage declares, applied or not. */
export function analysisControlParamNames(
  sheet: AnalysisSheetRecipe,
  sheets?: Record<string, AnalysisSheetRecipe>,
): string[] {
  const names: string[] = [];
  const visited = new Set<string>();
  let current: AnalysisSheetRecipe | undefined = sheet;
  while (current && !visited.has(current.id)) {
    visited.add(current.id);
    current.controls.forEach((control) => {
      if (!names.includes(control.paramName)) names.push(control.paramName);
    });
    const parentId: string | undefined = current.breakdown?.parentSheetId;
    current = parentId ? sheets?.[parentId] : undefined;
  }
  return names;
}

/**
 * A readable label per control param, for surfaces that show the param to a
 * reader rather than to the backend.
 *
 * The param *name* is a wire identifier — the backend binds `{param: name}` by
 * it — and it is generated, so a published card's filter row was labelled
 * `analysis_breakdown_msgq4iqb_cycox_analysis__000062…`. The control's own field
 * carries the label the sheet shows, so that is what a reader gets.
 */
export function analysisControlParamLabels(
  sheet: AnalysisSheetRecipe,
  sheets?: Record<string, AnalysisSheetRecipe>,
  labelFor: (field: string) => string = (field) => field,
): Record<string, string> {
  const labels: Record<string, string> = {};
  const visited = new Set<string>();
  let current: AnalysisSheetRecipe | undefined = sheet;
  while (current && !visited.has(current.id)) {
    visited.add(current.id);
    current.controls.forEach((control) => {
      if (labels[control.paramName] === undefined) {
        labels[control.paramName] = labelFor(control.field);
      }
    });
    const parentId: string | undefined = current.breakdown?.parentSheetId;
    current = parentId ? sheets?.[parentId] : undefined;
  }
  return labels;
}

/** Graph caps mirror the reMap canvas budget the backend defaults to. */
export const ANALYSIS_GRAPH_MAX_NODES = 1000;
export const ANALYSIS_GRAPH_MAX_RELATIONSHIPS = 5000;

export interface CompiledAnalysisGraph {
  /** Null when the recipe's result cannot be expressed as a Graph. */
  pipeline: Pipeline | null;
  executable: boolean;
  missingCapabilities: Array<keyof AnalysisBackendCapabilities>;
  /** Why a Graph is impossible for this recipe, in the user's terms. */
  blockers: string[];
}

/**
 * Reasons a recipe's rows cannot be published as a graph. `to_graph` walks the
 * source node the RowSet is still bound to, and an aggregation projects that
 * node away — so a rolled-up or grouped result is a table of values with no
 * nodes left to draw. Lookups and rollups both aggregate (grain-preserving
 * `group_rows`), which is why they block a graph too.
 */
/**
 * Whether the sheet decides which rows to keep using a related-data column.
 *
 * Those conditions can only run after the aggregation that produces the column,
 * so the surviving row set — and therefore the node set — is not knowable from
 * the pre-grouping chain a graph is drawn from. Narrowed conditions are read at
 * source grain and do not count; nor do transient column filters, which are not
 * part of the durable recipe a published card replays.
 */
const analysisKeepsOnMadeColumns = (sheet: AnalysisSheetRecipe): boolean => {
  // Only a sheet that gathers related data grows a `group_rows`, and only then
  // does the graph chain get truncated. Without one, nothing is dropped — a
  // calculation and a condition over it both survive into the graph — so there
  // is nothing to refuse. Calc columns still count BELOW when a lookup or
  // rollup is present, because their `derive_rows` runs after the grouping.
  const aggregates = sheet.steps.some(
    (step) => step.kind === 'lookup' || step.kind === 'rollup',
  );
  if (!aggregates) return false;
  const madeFields = new Set(sheet.steps.flatMap((step) => step.outputColumns));
  if (madeFields.size === 0) return false;
  const keep = partitionConditionGroup(
    conditionGroupFromJson(sheet.keepFilter),
    madeFields,
  );
  // `made` is a ConditionGroup, not a list — an empty one means nothing in the
  // Keep filter reads a Made column.
  if (keep.made.conditions.length > 0) return true;
  // An `any` group spanning both grains cannot be split, so the compiler marks
  // it unsupported and keeps the whole filter post-group. The node set depends
  // on it either way.
  if (keep.unsupported) return true;
  return appliedControlPredicates(sheet).some((predicate) =>
    predicateReadsMadeField(predicate, madeFields),
  );
};

export function analysisGraphBlockers(
  sheet: AnalysisSheetRecipe,
  sheets?: Record<string, AnalysisSheetRecipe>,
): string[] {
  const blockers: string[] = [];
  if (!sheet.source.label) {
    blockers.push('This sheet has no source label to draw nodes from');
  }
  if (sheet.breakdown) {
    blockers.push(
      'A grouped sheet has one row per group, not per node, so it has no graph to publish',
    );
  }
  // Related-data columns no longer block a graph. They are grain-preserving —
  // one row per source node, which is what the authoring panel promises with
  // "your rows stay N, related data is added as columns, never as extra rows" —
  // so WHICH nodes the sheet holds is already settled before they are gathered.
  // `compileAnalysisGraph` therefore draws the graph from the chain before the
  // grouping, rather than appending `to_graph` to a grouped RowSet, which the
  // backend refuses outright ("to_graph needs a RowSet still bound to its base
  // node … before any group_rows / window_rows / join").
  //
  // A Kept condition over one of those columns is the exception, and it is a
  // real one: the filter can only run after the aggregation, so the surviving
  // node set is not knowable from the earlier chain.
  if (analysisJoinStep(sheet.steps)) {
    blockers.push(
      'This sheet matches records by value rather than by relationship, so its rows are pairs of records rather than the records themselves — there is no single node set to draw',
    );
  }
  if (analysisKeepsOnMadeColumns(sheet)) {
    blockers.push(
      'This sheet keeps rows based on a related-data column, so which records it contains is only decided after that data is gathered — publish it as a table, or move the condition to Narrowed',
    );
  }
  const parentId = sheet.breakdown?.parentSheetId;
  const parent = parentId ? sheets?.[parentId] : undefined;
  if (parent && parent.steps.some((step) => step.kind !== 'calc')) {
    blockers.push(
      'The parent sheet aggregates related data, so the grouped rows cannot be drawn',
    );
  }
  return blockers;
}

/**
 * Compiles a sheet into a Graph-carrier pipeline: the same narrowing, controls
 * and calculations, ending in an induced `to_graph` instead of `to_table`. The
 * induced subgraph is exactly the sheet's own rows and the edges between them,
 * so a published card draws what the sheet selected rather than pulling in
 * every neighbour of a hub node.
 */
export function compileAnalysisGraph(
  sheet: AnalysisSheetRecipe,
  sheets: Record<string, AnalysisSheetRecipe> | undefined,
  options: CompileAnalysisRecipeOptions,
): CompiledAnalysisGraph {
  const blockers = analysisGraphBlockers(sheet, sheets);
  const compiled = compileAnalysisSheet(sheet, sheets, {
    ...options,
    target: 'publication',
    sort: null,
    offset: 0,
    limit: undefined,
  });
  if (blockers.length > 0) {
    return {
      pipeline: null,
      executable: false,
      missingCapabilities: compiled.missingCapabilities,
      blockers,
    };
  }
  // Stop before the first step that leaves the base node behind. `to_graph`
  // walks the source node `n` to project the neighbourhood, and the backend
  // rejects a RowSet that no longer has it bound:
  //
  //   to_graph needs a RowSet still bound to its base node (run it directly
  //   off rows_from_nodes, before any group_rows / window_rows / join)
  //
  // `match_related` and the `grain_preserved` `group_rows` that gathers
  // related data are exactly those steps. Cutting there is not a compromise:
  // related-data columns are grain-preserving, so the node set is already
  // decided by this point, and anything that would change it — a Kept
  // condition over one of those columns — is refused by
  // `analysisKeepsOnMadeColumns` before we get here.
  const steps: typeof compiled.pipeline.steps = [];
  for (const step of compiled.pipeline.steps) {
    if (
      step.op === 'to_table' ||
      step.op === 'match_related' ||
      step.op === 'group_rows' ||
      step.op === 'window_rows'
    ) {
      break;
    }
    steps.push(step);
  }
  const from = steps[steps.length - 1]?.as ?? 'source_rows';
  steps.push({
    op: 'to_graph',
    from,
    args: {
      max_level: 1,
      max_nodes: ANALYSIS_GRAPH_MAX_NODES,
      max_relationships: ANALYSIS_GRAPH_MAX_RELATIONSHIPS,
      induced: true,
    },
    as: 'out',
  });
  return {
    pipeline: { ...compiled.pipeline, steps },
    executable: compiled.missingCapabilities.length === 0,
    missingCapabilities: compiled.missingCapabilities,
    blockers,
  };
}

export interface AnalysisPublicationSupport extends AnalysisPublicationTargets {
  /** Per-target explanation shown when that target is unavailable. */
  reasons: Partial<Record<keyof AnalysisPublicationTargets, string>>;
}

/**
 * What the sheet on screen may be published as. Execution and publication stay
 * separate contracts: a runnable sheet can still be unpublishable as a graph,
 * and a sheet that cannot run is publishable as nothing.
 */
export function analysisPublicationSupport(input: {
  compiled: CompiledAnalysisRecipe | null;
  graph?: CompiledAnalysisGraph | null;
  verified?: AnalysisPublicationTargets;
}): AnalysisPublicationSupport {
  const verified = input.verified ?? ANALYSIS_PUBLICATION_VERIFIED;
  const reasons: AnalysisPublicationSupport['reasons'] = {};
  const runnable = Boolean(input.compiled?.executable);
  const missing = input.compiled?.missingCapabilities ?? [];
  const unrunnableReason = missing.length
    ? `This sheet cannot run against the deployed backend yet (${missing.join(', ')}), so there is nothing to publish`
    : 'This sheet has no runnable recipe to publish';

  const tabular = runnable && verified.table;
  if (!runnable) {
    reasons.table = unrunnableReason;
    reasons.chart = unrunnableReason;
    reasons.kpi = unrunnableReason;
  } else if (!verified.table) {
    reasons.table = 'Publishing this sheet as a table card is not verified yet';
  }

  const graphBlockers = input.graph?.blockers ?? [];
  const graph =
    runnable &&
    verified.graph &&
    Boolean(input.graph?.pipeline) &&
    Boolean(input.graph?.executable);
  if (!graph) {
    reasons.graph = !runnable
      ? unrunnableReason
      : graphBlockers.length > 0
        ? graphBlockers[0]
        : 'Publishing this sheet as a graph card is not verified yet';
  }

  const fragment = runnable && verified.fragment;
  if (!fragment) {
    reasons.fragment = runnable
      ? ANALYSIS_FRAGMENT_UNVERIFIED_REASON
      : unrunnableReason;
  }

  return {
    table: tabular,
    chart: runnable && verified.chart,
    kpi: runnable && verified.kpi,
    graph,
    fragment,
    reasons,
  };
}
